import merge from 'lodash/merge';
import { useContext, useEffect, useCallback } from 'react';
import { useUniqueId } from '../hooks/useUniqueId';
import { AnalyticsContext } from './AnalyticsContext';
import { TrackingData } from './TrackingData';
import { TrackingEvent } from './TrackingEvent';

/**
 * @param fragment — an optional fragment of analytics state, which will be combined with
 * all other such fragments and attached to any tracking events which occur.
 */
export const useAnalytics = (fragment: TrackingData = {}): HookResult => {
  const { bus, state } = useContext(AnalyticsContext);
  const nodeId = useUniqueId();

  /** We add the fragment directly within the render cycle rather than in an effect.
   * This is safe because adding or updating a fragment does not cause a re-render,
   * and it is useful because effects which trigger an analytics event from anywhere in the tree
   * will always see all the fragments which have been collected.
   */
  state.addFragment(nodeId, fragment);
  useEffect(() => () => state.removeFragment(nodeId), [nodeId, state]);

  const trackEvent = useCallback(
    (name: TrackingEvent, extraData?: TrackingData) => {
      const data = state.build();
      if (extraData) merge(data, extraData);
      bus.send({ name, data });
    },
    [bus, state],
  );

  return { trackEvent };
};

interface HookResult {
  /**
   * Fires a named analytics event, which will be combined with all the current analytics state and
   * passed to the analytics dispatchers (Adobe, Braze etc)
   * @param eventName — the name of the event to fire
   * @param extraData — an optional fragment of analytics state, which will be
   * attached to this event only.
   */
  trackEvent(eventName: TrackingEvent, extraData?: TrackingData): void;
}
